import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Alert, Typography } from 'antd';
import { UserOutlined, LoginOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuth from '../../auth/useAuth';
import { logger } from '../../utils/logger';
import './Login.css';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const { oktaAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If authentication is in progress from callback, don't interfere
    const authInProgress = sessionStorage.getItem('auth_in_progress');
    if (authInProgress) {
      logger.debug('Authentication in progress, skipping Login checks');
      return;
    }

    // Check if we're returning from the unauthorized page
    const returningFromUnauthorized = sessionStorage.getItem('returning_from_unauthorized');

    if (returningFromUnauthorized) {
      logger.debug('Returning from unauthorized page, clearing flags...');
      // Clear only the return flag and auth error
      sessionStorage.removeItem('returning_from_unauthorized');
      sessionStorage.removeItem('auth_error');
      sessionStorage.removeItem('login_hint');

      // If there are error params in URL, clean them
      if (searchParams.get('error')) {
        navigate('/login', { replace: true });
      }
      return;
    }

    // Clear any previous auth errors when login page loads fresh
    sessionStorage.removeItem('auth_error');

    // Check for error in URL parameters from Okta redirect
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      // Only redirect to unauthorized for explicit access denial from Okta
      if (errorParam === 'access_denied') {
        sessionStorage.setItem('auth_error', 'access_denied');
        navigate('/unauthorized', { replace: true });
      } else if (errorParam === 'unauthorized_client') {
        sessionStorage.setItem('auth_error', 'unauthorized_client');
        navigate('/unauthorized', { replace: true });
      } else {
        // For other errors, show error message but stay on login page
        setError(errorDescription || `Authentication error: ${errorParam}`);
      }
    }
  }, [searchParams, navigate]);

  const validateCiscoEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@cisco\.com$/i;
    return emailRegex.test(email);
  };

  const handleLogin = async () => {
    setError('');

    // Validate email is required
    if (!email || email.trim() === '') {
      setError('Please enter your Cisco email address');
      return;
    }

    // Validate cisco.com domain
    if (!validateCiscoEmail(email)) {
      setError('Only cisco.com email addresses are allowed');
      return;
    }

    try {
      setLoading(true);

      // Complete cleanup before new login attempt
      logger.info('Starting fresh login', { email });

      // Clear only auth error flags
      sessionStorage.removeItem('auth_error');
      sessionStorage.removeItem('returning_from_unauthorized');

      // Store the new email for login hint
      sessionStorage.setItem('login_hint', email);

      logger.debug('Redirecting to Okta for authentication');

      // Redirect to Okta SSO login for authentication
      await oktaAuth.signInWithRedirect({
        originalUri: '/',
        loginHint: email,
      });
    } catch (err: unknown) {
      const loginErr = err as Record<string, unknown>;
      logger.error('Login error', loginErr);
      setError((loginErr.message as string) || 'Failed to initiate login. Please try again.');
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="login-container">
      <Card className="login-card" bordered={false}>
        <div className="login-header">
          <LoginOutlined className="login-icon" />
          <Title level={2}>Product Onboarding System</Title>
          <Text type="secondary">Sign in with your Cisco credentials</Text>
        </div>

        <Form layout="vertical" className="login-form">
          <Form.Item
            label="Cisco Email"
            required
            validateStatus={error ? 'error' : ''}
            help={error}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="username@cisco.com"
              size="large"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              onKeyPress={handleKeyPress}
              disabled={loading}
              autoFocus
            />
          </Form.Item>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item>
            <Button
              type="primary"
              size="large"
              block
              icon={<LoginOutlined />}
              onClick={handleLogin}
              loading={loading}
            >
              Sign in with Cisco SSO
            </Button>
          </Form.Item>

          <div className="login-footer">
            <Text type="secondary" style={{ fontSize: 12 }}>
              By signing in, you agree to the terms of service
            </Text>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
